import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

const NUMBERS = [["retention_years", "Keep applications for (years)", 1, 15],
                 ["pool_months", "Future-opportunity consent lasts (months)", 1, 60],
                 ["applicant_link_days", "Applicant link stays valid (days)", 1, 180]];

/** Careers and hiring policy: retention, consent period, the public statements and stage labels. */
export const HiringSettings = () => {
  const [form, setForm] = useState(null);
  const [comps, setComps] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/hiring/settings").then(({ data }) => setForm(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/hiring/screening/components").then(({ data }) => setComps(data.components)).catch(() => setComps([]));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/hiring/settings", {
        pool_enabled: form.pool_enabled, demographics_enabled: form.demographics_enabled,
        retention_years: Number(form.retention_years), pool_months: Number(form.pool_months),
        applicant_link_days: Number(form.applicant_link_days), careers_intro: form.careers_intro,
        eeo_statement: form.eeo_statement, privacy_notice: form.privacy_notice,
        stage_labels: form.stage_labels });
      setForm({ ...form, ...data });
      toast.success("Careers and hiring settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveComponents = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/hiring/screening/components", { components: comps });
      setComps(data.components);
      toast.success("Screening components saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!form) return <p className="text-muted-foreground">Loading hiring settings…</p>;

  return (
    <div className="space-y-5" data-testid="hiring-settings">
      <div className="bg-white rounded-2xl border p-6 space-y-5">
        <p className="text-sm text-muted-foreground">
          These values govern the public careers page, how long applicant information is kept, and how long a
          person stays in the future-opportunities pool before renewing their consent.
          <strong className="block mt-2 text-foreground">
            The retention period needs the daycare's written approval before you rely on it.
          </strong>
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          {NUMBERS.map(([key, label, min, max]) => (
            <div key={key}>
              <Label className="font-bold">{label}</Label>
              <Input type="number" min={min} max={max} className="mt-2" value={form[key]} data-testid={`hiring-${key}`}
                     onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
            </div>
          ))}
        </div>
        <div className="space-y-3">
          {[["pool_enabled", "Accept general expressions of interest", "Lets someone join the future-opportunities pool when nothing suitable is posted."],
            ["demographics_enabled", "Collect voluntary equal-opportunity answers", "Every question stays optional, is stored separately and is never shown to reviewers."]].map(([key, title, hint]) => (
            <div key={key} className="rounded-2xl border border-border p-5 flex items-center gap-4">
              <div className="flex-1"><p className="font-bold">{title}</p><p className="text-sm text-muted-foreground mt-1">{hint}</p></div>
              <Switch checked={Boolean(form[key])} data-testid={`hiring-${key}`}
                      onCheckedChange={(v) => setForm({ ...form, [key]: v })} />
            </div>
          ))}
        </div>
        <div>
          <Label className="font-bold">Careers page introduction</Label>
          <Textarea className="mt-2" value={form.careers_intro || ""} data-testid="hiring-intro"
                    onChange={(e) => setForm({ ...form, careers_intro: e.target.value })} />
        </div>
        <div>
          <Label className="font-bold">Equal employment opportunity statement</Label>
          <Textarea className="mt-2 min-h-32" value={form.eeo_statement || ""} data-testid="hiring-eeo"
                    onChange={(e) => setForm({ ...form, eeo_statement: e.target.value })} />
        </div>
        <div>
          <Label className="font-bold">Applicant privacy notice</Label>
          <Textarea className="mt-2 min-h-40" value={form.privacy_notice || ""} data-testid="hiring-privacy"
                    onChange={(e) => setForm({ ...form, privacy_notice: e.target.value })} />
        </div>
        <div>
          <Label className="font-bold">Stage names</Label>
          <p className="text-sm text-muted-foreground mt-1">
            Rename a stage to match how your team talks. The underlying record keeps its identifier, so history and
            reports are unaffected.
          </p>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {Object.entries(form.default_stage_labels || {}).map(([key, fallback]) => (
              <div key={key}>
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{fallback}</Label>
                <Input className="mt-1.5" value={form.stage_labels?.[key] ?? fallback} data-testid={`stage-label-${key}`}
                       onChange={(e) => setForm({ ...form, stage_labels: { ...form.stage_labels, [key]: e.target.value } })} />
              </div>
            ))}
          </div>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-full font-bold" data-testid="save-hiring-settings">
          Save careers and hiring settings
        </Button>
      </div>

      {comps && (
        <div className="bg-white rounded-2xl border p-6 space-y-4" data-testid="screening-components-settings">
          <div>
            <p className="font-bold">Background screening components</p>
            <p className="text-sm text-muted-foreground mt-1">
              The checks your team records for each applicant. This application does not run any check — confirm the
              current Arkansas and federal requirements with your licensing specialist and adjust this list to match.
            </p>
          </div>
          {comps.map((c, i) => (
            <div key={c.key} className="rounded-xl border border-border p-4 grid gap-3 sm:grid-cols-[1fr,1fr,7rem,5rem]">
              <Input value={c.label} placeholder="Check name" data-testid={`component-label-${i}`}
                     onChange={(e) => setComps(comps.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <Input value={c.authority || ""} placeholder="Authority" data-testid={`component-authority-${i}`}
                     onChange={(e) => setComps(comps.map((x, j) => (j === i ? { ...x, authority: e.target.value } : x)))} />
              <label className="flex items-center gap-2 text-sm font-semibold">
                <Switch checked={Boolean(c.required)} data-testid={`component-required-${i}`}
                        onCheckedChange={(v) => setComps(comps.map((x, j) => (j === i ? { ...x, required: v } : x)))} />
                Required
              </label>
              <Button variant="link" className="text-[#EF4444] px-0" data-testid={`component-remove-${i}`}
                      onClick={() => setComps(comps.filter((_, j) => j !== i))}>Remove</Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="rounded-full font-bold" data-testid="component-add"
                    onClick={() => setComps([...comps, { key: `check_${Date.now()}`, label: "", authority: "", required: true, note: "" }])}>
              Add a component
            </Button>
            <Button onClick={saveComponents} disabled={busy} className="rounded-full font-bold" data-testid="save-components">
              Save screening components
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" className="rounded-full"><Link to="/admin/hiring?tab=positions">Manage job postings<ArrowRight className="ml-2" /></Link></Button>
        <Button asChild variant="outline" className="rounded-full"><Link to="/careers">View the public careers page<ArrowRight className="ml-2" /></Link></Button>
      </div>
    </div>
  );
};
